import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';

/**
 * How many blanks are named before the rest are counted. Three is enough to
 * recognise what was skipped without turning the dialog into a second form.
 */
const NAMED_LIMIT = 3;

/** Bengali reads its own numerals, and these forms are printed in Bengali. */
const localizeCount = (count, language) => (
    String(language || '').startsWith('bn')
        ? String(count).replace(/\d/g, (digit) => '০১২৩৪৫৬৭৮৯'[Number(digit)])
        : String(count)
);

/**
 * Asked before a half-filled form is exported: it names what is still empty and
 * lets the applicant download anyway — printing a partly filled form to finish
 * by hand at the office is a normal way to use these tools.
 *
 * @param {{ open: boolean, fields: string[], loading?: boolean,
 *   onCancel: () => void, onConfirm: () => void }} props
 */
export default function MissingFieldsDialog({ open, fields, loading = false, onCancel, onConfirm }) {
    const { t, currentLanguage } = useTranslation();
    const named = fields.slice(0, NAMED_LIMIT);
    const rest = fields.length - named.length;

    return (
        <ConfirmDialog
            open={open}
            onClose={onCancel}
            onConfirm={onConfirm}
            loading={loading}
            variant="primary"
            title={t('missingFieldsDialog.title')}
            description={t('missingFieldsDialog.description')}
            confirmLabel={t('missingFieldsDialog.confirm')}
            cancelLabel={t('missingFieldsDialog.cancel')}
        >
            <ul className="w-full space-y-1 rounded-xl bg-orange-50/70 px-4 py-3 text-left dark:bg-orange-900/15">
                {named.map((label, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`${label}-${index}`} className="text-[12.5px] text-zinc-700 dark:text-zinc-200">
                        • {label}
                    </li>
                ))}
                {rest > 0 ? (
                    <li className="text-[12.5px] font-semibold text-orange-600 dark:text-orange-300">
                        {t('missingFieldsDialog.more', { n: localizeCount(rest, currentLanguage) })}
                    </li>
                ) : null}
            </ul>
        </ConfirmDialog>
    );
}
